import { ChangeDetectionStrategy, Component, OnDestroy, ViewChild } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatStepper } from '@angular/material/stepper';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, first, map, tap } from 'rxjs/operators';

import { candidatesUpdate } from '@collections/candidates';
import { areAssignsCompleted } from '@modules/assigns/store';
import { DangerAlert, SuccessAlert } from '@shared/alert';
import * as FormActions from '@shared/form/store/form.actions';
import { isTruthy, sleep } from '@shared/utils';

import { SourceStatus } from '../../../+details/+source/candidate-source.component';
import {
  CandidatesUploadGet,
  CandidatesUploadSelect,
  getUploadCandidateId,
  UploadResume,
} from '../../store';

// TODO: this component looks like it does much less than its implemented here
// verify what exact code is needed and remove rest
// for example in template we have mat-stepper but it doesn't seem to be used to display steps
@Component({
  selector: 'mojo-candidate-upload',
  templateUrl: './candidate-upload.container.html',
  styleUrls: ['./candidate-upload.container.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CandidateUploadComponent implements OnDestroy {
  @ViewChild('stepper')
  stepper: MatStepper; // TODO: is this used?

  assigning = false; // TODO: is this used?
  form = new UntypedFormGroup({
    email: new UntypedFormControl(null, [Validators.email, Validators.required]),
    file: new UntypedFormControl(null, Validators.required),
    source: new UntypedFormControl(null, Validators.required),
    sourceNote: new UntypedFormControl('', Validators.required),
    noteComment: new UntypedFormControl(),
  });
  selectedCandidateId$: Observable<number> = this.store
    .select(getUploadCandidateId)
    .pipe(filter(isTruthy), first());
  create$: Observable<boolean> = this.store.select(getUploadCandidateId).pipe(
    first(),
    map((id) => !id),
  );

  constructor(private store: Store<{}>) {}

  ngOnDestroy(): void {
    this.store.dispatch(new CandidatesUploadSelect());
  }

  isBasicInfoCompleted(): boolean {
    if (this.form.controls.email.valid && this.form.controls.source.valid) {
      if ([SourceStatus.Other, SourceStatus.Referral].includes(this.form.controls.source.value)) {
        return this.form.controls.sourceNote.valid;
      } else {
        return true;
      }
    }
    return false;
  }

  validateBasicInfo() {
    if (!this.isBasicInfoCompleted()) {
      this.form.controls.email.markAsTouched();
      this.form.controls.source.markAsTouched();
      this.form.controls.sourceNote.markAsTouched();
      this.store.dispatch(
        new DangerAlert('Correct the errors below before moving to the next step.'),
      );
    }
  }

  validate(control: AbstractControl): boolean {
    if (control.invalid) {
      control.markAsTouched();
      this.store.dispatch(
        new DangerAlert('Correct the errors below before moving to the next step.'),
      );
    }
    return control.valid;
  }

  getCandidateId() {
    this.store.dispatch(new CandidatesUploadGet({ emailId: this.form.controls.email.value }));
  }

  updateCandidateDetails() {
    this.selectedCandidateId$
      .pipe(
        tap((id) =>
          this.store.dispatch(
            candidatesUpdate({
              payload: {
                id,
                source: this.form.controls.source.value,
                sourceNote: this.form.controls.sourceNote.value,
              },
            }),
          ),
        ),
      )
      .subscribe();
  }

  uploadResume() {
    this.selectedCandidateId$
      .pipe(
        tap((id) =>
          this.store.dispatch(
            new UploadResume({
              file: this.form.controls.file.value,
              id,
            }),
          ),
        ),
      )
      .subscribe();
  }

  submit() {
    if (this.validate(this.form.controls.file)) {
      this.getCandidateId();
      this.updateCandidateDetails();
      this.uploadResume();
    }
  }

  close() {
    this.store.dispatch(FormActions.formClose({}));
  }

  onAssignDone(jobTitles: string[], isNewCandidateCreated: boolean) {
    this.store
      .select(areAssignsCompleted)
      .pipe(filter(isTruthy), first())
      .subscribe(() => {
        this.dispatchSuccessAlert(jobTitles, isNewCandidateCreated);
        this.redirectToCandidatePage(!isNewCandidateCreated, isNewCandidateCreated ? 3000 : 1000);
      });
  }

  private redirectToCandidatePage(redirect: boolean, timeout: number) {
    sleep(timeout).then(() => {
      this.selectedCandidateId$
        .pipe(
          tap((id) => {
            this.store.dispatch(
              FormActions.formSubmit({
                redirectUrl: `/candidates/${id}/${redirect ? 'assigns' : ''}`,
              }),
            );
          }),
        )
        .subscribe();
    });
  }

  private dispatchSuccessAlert(jobTitles: string[], isNewCreated: boolean): void {
    const jobs = jobTitles.join(',');
    const alertText = !isNewCreated
      ? 'Candidate has been moved to shortlists of : ' + jobs
      : !jobs
      ? 'Candidate has been created'
      : 'Candidate has been created and moved to shortlists of ' + jobs;

    this.store.dispatch(new SuccessAlert(alertText));
  }
}
